import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  PieChart, Pie, Cell, Tooltip, ResponsiveContainer,
} from 'recharts';
import { api } from '../hooks/useApi';
import DateInput from '../components/DateInput';
import { SectionProduitFini } from '../components/TracabiliteSections';
import EtapeTracabilite from '../components/EtapeTracabilite';
import { creerFormulaireTraiteFinal } from '../utils/tracabilite';
import BatchAnalytiquePanel from '../components/BatchAnalytiquePanel';
import CoutRevient from '../components/CoutRevient';
import CourbeCuissonBatch from '../components/CourbeCuissonBatch';
import RecetteStockPanel from '../components/RecetteStockPanel';
import Icon from '../components/Icon';
import './ProductionBrisket.css';

// ─── CONSTANTES ──────────────────────────────

const ETAPES = [
  { id: 1, label: 'En-tête' },
  { id: 2, label: 'Parage' },
  { id: 3, label: 'Traçabilité' },
  { id: 4, label: 'Cuisson' },
  { id: 5, label: 'Découpe' },
  { id: 6, label: 'Finalisation' },
];

const COULEURS_REPARTITION = {
  tranches  : 'var(--success)',
  chopped   : 'var(--accent)',
  burnt_ends: 'var(--danger)',
  pertes    : 'var(--muted)',
};

// ─── STEPPER ─────────────────────────────────

function Stepper({ etape, nbTracabiliteNonLies = 0, onStepClick, hasBatch, isFinalise }) {
  const peutCliquer = (cible) => {
    if (!hasBatch && cible > 1) return false;
    if (cible < etape) return true;
    if (isFinalise) return true;
    if (cible <= etape) return true;
    return etape >= cible - 1;
  };

  return (
    <div className="stepper">
      {ETAPES.map((e, i) => {
        const clickable = onStepClick && peutCliquer(e.id);
        const estFait = etape > e.id;
        return (
          <React.Fragment key={e.id}>
            <div
              className={`step ${etape === e.id ? 'active' : estFait ? 'done' : ''} ${clickable ? 'step-cliquable' : ''}`}
              style={{ position: 'relative', cursor: clickable ? 'pointer' : 'default' }}
              onClick={() => clickable && onStepClick(e.id)}
              title={clickable ? `Aller à l'étape ${e.id} — ${e.label}` : ''}
            >
              <div className="step-circle">
                {estFait ? <Icon name="check" size={14} weight="bold" /> : e.id}
              </div>
              {e.id === 3 && nbTracabiliteNonLies > 0 && (
                <span className="step-badge-warning">{nbTracabiliteNonLies}</span>
              )}
              <span className="step-label">{e.label}</span>
            </div>
            {i < ETAPES.length - 1 && (
              <div className={`step-line ${estFait ? 'done' : ''}`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ─── BADGE CONFIANCE ─────────────────────────

function BadgeConfiance({ confiance }) {
  const mod = confiance === 'élevée' ? 'success' : confiance === 'moyenne' ? 'warning' : '';
  return <span className={`badge${mod ? ` badge--${mod}` : ''}`}>{confiance}</span>;
}

// ─── PANNEAU TIMER CUISSON BRISKET ───────────

function PanneauTimerCuissonBrisket({ batchId, batchData, dateProd, onRefresh }) {
  const [chargement, setChargement] = useState(false);
  const [erreur, setErreur]         = useState('');
  const [tempsEcoule, setTempsEcoule] = useState('');
  const [heureManuelle, setHeureManuelle] = useState('');

  useEffect(() => {
    if (!batchData?.heure_debut || batchData?.heure_fin) return;
    const calculer = () => {
      const debut = new Date(batchData.heure_debut);
      const diffMs = new Date() - debut;
      if (diffMs < 0) return;
      const h = Math.floor(diffMs / 3600000);
      const m = Math.floor((diffMs % 3600000) / 60000);
      setTempsEcoule(`${h}h ${String(m).padStart(2, '0')}min`);
    };
    calculer();
    const iv = setInterval(calculer, 30000);
    return () => clearInterval(iv);
  }, [batchData?.heure_debut, batchData?.heure_fin]);

  const fmtH = (iso) => iso ? new Date(iso).toLocaleTimeString('fr-CA', { hour: '2-digit', minute: '2-digit' }) : '—';
  const duree = (d, f) => {
    if (!d || !f) return '';
    const ms = new Date(f) - new Date(d);
    if (ms < 0) return '';
    return `${Math.floor(ms / 3600000)}h ${String(Math.floor((ms % 3600000) / 60000)).padStart(2, '0')}min`;
  };

  const action = async (type, extra) => {
    setChargement(true); setErreur('');
    try {
      await api.patch(`/batches/${batchId}/cuisson/${type}`, extra || {});
      onRefresh();
    } catch (e) { setErreur(e.response?.data?.error || 'Erreur'); }
    finally { setChargement(false); }
  };

  return (
    <div className="card timer-cuisson-panel" style={{ marginBottom: 16 }}>
      <div className="timer-cuisson-header">
        <Icon name="timer" size={16} color="var(--muted)" />
        <span className="timer-cuisson-title">Suivi cuisson</span>
      </div>
      {erreur && (
        <div className="error-inline" style={{ marginBottom: 12 }}>
          <Icon name="erreur" size={15} />
          {erreur}
        </div>
      )}
      {!batchData?.heure_debut && (
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <button className="btn btn-primary" onClick={() => action('demarrer')} disabled={chargement}>
            <Icon name="demarrer" size={15} />
            {chargement ? 'Démarrage...' : 'Démarrer la cuisson'}
          </button>
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
            <div className="field" style={{ marginBottom: 0 }}>
              <label style={{ fontSize: 11 }}>Ou heure manuelle</label>
              <input type="time" value={heureManuelle} onChange={e => setHeureManuelle(e.target.value)} style={{ width: 130 }} />
            </div>
            {heureManuelle && (
              <button className="btn btn-ghost btn--sm" disabled={chargement}
                onClick={() => action('demarrer', { heure_debut: new Date(`${dateProd}T${heureManuelle}:00`).toISOString() })}>
                Enregistrer
              </button>
            )}
          </div>
        </div>
      )}
      {batchData?.heure_debut && !batchData?.heure_fin && (
        <div>
          <div style={{ display: 'flex', gap: 16, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
            <span className="timer-running-badge">
              <Icon name="demarrer" size={13} />
              Démarré à {fmtH(batchData.heure_debut)}
            </span>
            {tempsEcoule && <span className="timer-elapsed">{tempsEcoule}</span>}
          </div>
          <button className="btn btn-danger" onClick={() => action('arreter')} disabled={chargement}>
            <Icon name="arreter" size={15} />
            {chargement ? 'Arrêt...' : 'Arrêter la cuisson'}
          </button>
        </div>
      )}
      {batchData?.heure_debut && batchData?.heure_fin && (
        <div className="timer-done-info">
          <span className="muted">Démarré {fmtH(batchData.heure_debut)} → Terminé {fmtH(batchData.heure_fin)}</span>
          <span className="gold" style={{ fontWeight: 600, fontFamily: 'Cinzel, serif', fontSize: 14 }}>
            Durée : {duree(batchData.heure_debut, batchData.heure_fin)}
          </span>
        </div>
      )}
    </div>
  );
}

// ─── ÉTAPE 1 BIS — Édition d'un batch existant ─

function EtapeBatchEditerBrisket({ batchId, batchData, isFinalise, onUpdated }) {
  const [form, setForm] = useState({
    nom                  : batchData?.nom || '',
    date_prod            : batchData?.date_prod || '',
    operateurs           : batchData?.operateurs || '',
    fournisseur          : batchData?.fournisseur || '',
    prix_kg              : batchData?.prix_kg || '',
    nb_briskets          : batchData?.nb_briskets || '',
    poids_brut_initial_kg: batchData?.poids_brut_initial_kg || batchData?.poids_cru_total_kg || '',
    notes                : batchData?.notes || '',
  });

  useEffect(() => {
    if (batchData) {
      setForm({
        nom                  : batchData.nom || '',
        date_prod            : batchData.date_prod || '',
        operateurs           : batchData.operateurs || '',
        fournisseur          : batchData.fournisseur || '',
        prix_kg              : batchData.prix_kg || '',
        nb_briskets          : batchData.nb_briskets || '',
        poids_brut_initial_kg: batchData.poids_brut_initial_kg || batchData.poids_cru_total_kg || '',
        notes                : batchData.notes || '',
      });
    }
  }, [batchData?.id]); // eslint-disable-line

  const [loading, setLoading] = useState(false);
  const [erreur, setErreur]   = useState('');
  const [toast, setToast]     = useState('');

  const enregistrer = async () => {
    setLoading(true);
    setErreur('');
    setToast('');
    try {
      const updated = await api.put(`/brisket/batches/${batchId}`, {
        nom                  : form.nom || null,
        date_prod            : form.date_prod || null,
        operateurs           : form.operateurs || null,
        fournisseur          : form.fournisseur || null,
        prix_kg              : form.prix_kg !== '' ? parseFloat(form.prix_kg) : null,
        nb_briskets          : form.nb_briskets !== '' ? parseInt(form.nb_briskets) : null,
        notes                : form.notes || null,
      });
      onUpdated(updated);
      setToast('Modifications enregistrées ✓');
      setTimeout(() => setToast(''), 3000);
    } catch (e) {
      setErreur(e.response?.data?.error || 'Erreur enregistrement');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="card form-card">
      <div className="card-section-header">
        <Icon name="modifier" size={15} color="var(--muted)" />
        <span className="form-titre">Modifier les informations — {batchData?.nom || form.nom}</span>
        {isFinalise && (
          <span className="badge badge--success">Finalisé</span>
        )}
      </div>
      {erreur && (
        <div className="error-inline" style={{ marginBottom: 12 }}>
          <Icon name="erreur" size={15} />{erreur}
        </div>
      )}
      {toast && (
        <div className="alert alert-success" style={{ marginBottom: 12 }}>
          <Icon name="succes" size={15} />{toast}
        </div>
      )}

      <div className="form-grid">
        <div className="field">
          <label>Numéro du batch</label>
          <input value={form.nom} onChange={e => setForm({ ...form, nom: e.target.value })} />
        </div>
        <div className="field">
          <label>Date de production</label>
          <DateInput value={form.date_prod} onChange={v => setForm({ ...form, date_prod: v })} />
        </div>
        <div className="field">
          <label>Opérateurs</label>
          <select value={form.operateurs} onChange={e => setForm({ ...form, operateurs: e.target.value })}>
            <option value="">— Sélectionner —</option>
            <option value="LCU">LCU</option>
            <option value="ELA">ELA</option>
            <option value="LCU+ELA">Les deux</option>
          </select>
        </div>
        <div className="field">
          <label>Fournisseur</label>
          <input value={form.fournisseur} onChange={e => setForm({ ...form, fournisseur: e.target.value })} placeholder="Boucherie..." />
        </div>
        <div className="field">
          <label>Prix d'achat ($/kg)</label>
          <input type="number" step="0.01" value={form.prix_kg}
            onChange={e => setForm({ ...form, prix_kg: e.target.value })} />
        </div>
        <div className="field">
          <label>Nb briskets</label>
          <input type="number" step="1" value={form.nb_briskets}
            onChange={e => setForm({ ...form, nb_briskets: e.target.value })} />
        </div>
        <div className="field">
          <label>Poids brut initial (kg)</label>
          <input type="number" step="0.01" value={form.poids_brut_initial_kg}
            disabled
            title="Poids brut figé après création du batch" />
          <span className="muted" style={{ fontSize: 11 }}>Verrouillé après création</span>
        </div>
      </div>

      <div className="field full">
        <label>Notes</label>
        <textarea rows={2} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} />
      </div>

      <div className="form-actions">
        <button type="button" className="btn btn-primary" onClick={enregistrer} disabled={loading}>
          {loading ? 'Enregistrement...' : 'Enregistrer les modifications'}
        </button>
      </div>
      {isFinalise && (
        <p className="muted" style={{ fontSize: 12, marginTop: 8 }}>
          Batch finalisé : champs analytiques verrouillés. Date, opérateurs, fournisseur, prix d'achat et notes restent éditables.
        </p>
      )}
    </div>
  );
}

// ─── COMPOSANT PRINCIPAL ─────────────────────

export default function ProductionBrisket() {
  const [onglet, setOnglet]   = useState('production');
  const [etape, setEtape]     = useState(1);
  const [batches, setBatches] = useState([]);
  const [batchAnalytique, setBatchAnalytique] = useState(null);
  const [stats, setStats]     = useState(null);
  const [margeCible, setMargeCible] = useState(60);
  const [msg, setMsg]         = useState('');
  const [erreur, setErreur]   = useState('');
  const [loading, setLoading] = useState(false);

  // Batch courant
  const [batchId, setBatchId] = useState(null);
  const [batchData, setBatchData] = useState(null);

  // ─── Auto-numérotation brisket
  const [nomAuto, setNomAuto] = useState('');
  const [nomManuel, setNomManuel] = useState(false);

  useEffect(() => {
    api.get('/batches/prochain-numero/brisket')
      .then(r => {
        setNomAuto(r.nom);
        setForm1(f => ({ ...f, nom: f.nom || r.nom }));
      })
      .catch(() => {});
    // Charger la marge cible depuis app_settings
    api.get('/settings')
      .then(settings => {
        const s = Array.isArray(settings) ? settings : [];
        const found = s.find(x => x.cle === 'marge_cible_pct');
        if (found && found.valeur) setMargeCible(parseFloat(found.valeur) || 60);
      })
      .catch(() => {});
  }, []); // eslint-disable-line

  // ─── Étape 1 — Infos batch
  const [form1, setForm1] = useState({
    nom                  : '',
    date_prod            : new Date().toISOString().slice(0, 10),
    operateurs           : '',
    fournisseur          : '',
    prix_kg              : '',
    nb_briskets          : '',
    poids_brut_initial_kg: '',
    notes                : '',
  });

  // ─── Étape 2 — Parage
  const [formParage, setFormParage] = useState({
    poids_parures_kg: '',
    poids_equipe_kg : '',
    poids_perte_kg  : '',
  });
  const [parageLoading, setParageLoading] = useState(false);
  const [parageMsg, setParageMsg]         = useState('');
  const [parageErreur, setParageErreur]   = useState('');
  const [poidsPareAffiche, setPoidsPareAffiche] = useState(null);

  // ─── Étape 4 — Cuisson
  const [form2, setForm2] = useState({
    poids_apres_cuisson_kg  : '',
    heures_cuisson          : '',
    temp_interne_f          : '',
    poids_tallow_wrapping_ml: '',
    notes_qualite           : '',
  });

  // ─── Étape 5 — Découpe
  // Tranches : saisie poids total + nb paquets → calculateur moyen → LCU fixe arrondi + prix
  const [poidsTotalTranchesG, setPoidsTotalTranchesG] = useState('');
  const [nbPaquetsTranches, setNbPaquetsTranches]     = useState('');
  const [poidsArrondiPaquetG, setPoidsArrondiPaquetG] = useState('');
  const [prixPaquet, setPrixPaquet]                   = useState('');
  // Chopped (350 g) + Burnt ends (200 g) — spec 2026-05-31
  const [nbChopped, setNbChopped]         = useState('');
  const [nbBurntEnds, setNbBurntEnds]     = useState('');
  // Prix par paquet (override la valeur par défaut du catalogue) — saisis ou auto-remplis depuis la reco
  const [prixChopped, setPrixChopped]     = useState('');
  const [prixBurntEnds, setPrixBurntEnds] = useState('');

  // Aperçu déduction + analytique
  const [apercuDeduction, setApercuDeduction]   = useState(null);
  const [deductionsFinales, setDeductionsFinales] = useState([]);
  const [analytiqueFinal, setAnalytiqueFinal]    = useState(null);
  const [recosPrix, setRecosPrix]                = useState(null);
  const [finalForm, setFinalForm] = useState(() => creerFormulaireTraiteFinal('brisket'));
  const [nbTracabiliteNonLies, setNbTracabiliteNonLies] = useState(0);
  const submittingRef = useRef(false);

  // ─── Planificateur
  const [planKg, setPlanKg]         = useState('5');
  const [planFormat, setPlanFormat] = useState('chopped');
  const [planResult, setPlanResult] = useState(null);
  const [planLoading, setPlanLoading] = useState(false);

  // ─── Chargement données ───────────────────

  const chargerBatches = useCallback(() => {
    api.get('/brisket/batches').then(setBatches).catch(() => {});
  }, []);

  const chargerStats = useCallback(() => {
    api.get('/brisket/yield/stats').then(setStats).catch(() => {});
  }, []);

  useEffect(() => {
    chargerBatches();
    chargerStats();
  }, [chargerBatches, chargerStats]);

  // Recharger batch courant si batchId change
  useEffect(() => {
    if (batchId) {
      api.get(`/brisket/batches/${batchId}`).then(d => {
        setBatchData(d);
        // Pré-remplir parage si déjà sauvegardé
        if (d.poids_parures_kg != null) {
          setFormParage({
            poids_parures_kg: String(d.poids_parures_kg || ''),
            poids_equipe_kg : String(d.poids_equipe_kg  || ''),
            poids_perte_kg  : String(d.poids_perte_kg   || ''),
          });
          const ppare = d.poids_pare_pre_cuisson_kg ?? d.poids_pare;
          if (ppare != null) setPoidsPareAffiche(ppare);
        }
        // Pré-remplir cuisson si déjà sauvegardée
        if (d.poids_apres_cuisson_kg != null) {
          setForm2(f => ({
            ...f,
            poids_apres_cuisson_kg  : String(d.poids_apres_cuisson_kg || ''),
            heures_cuisson          : String(d.heures_cuisson || ''),
            temp_interne_f          : String(d.temp_interne_f || ''),
            poids_tallow_wrapping_ml: String(d.poids_tallow_wrapping_ml || ''),
            notes_qualite           : d.notes_qualite || '',
          }));
        }
        // Pré-remplir Découpe (étape 5) depuis brisket_repartition + inventaire_produits
        if (d.repartition) {
          const r = d.repartition;
          if (r.poids_total_tranches_g) setPoidsTotalTranchesG(String(r.poids_total_tranches_g));
          if (r.nb_sacs_tranches)       setNbPaquetsTranches(String(r.nb_sacs_tranches));
          if (r.prix_tranches)          setPrixPaquet(String(r.prix_tranches));
          if (r.nb_sacs_chopped)        setNbChopped(String(r.nb_sacs_chopped));
          if (r.nb_sacs_burnt_ends)     setNbBurntEnds(String(r.nb_sacs_burnt_ends));
        }
        // Prix saisi par format à la finalisation (depuis inventaire_produits)
        if (Array.isArray(d.produits)) {
          const ch = d.produits.find(p => p.sku === 'BE350');
          const be = d.produits.find(p => p.sku === 'BEND250');
          if (ch?.prix_unitaire) setPrixChopped(String(ch.prix_unitaire));
          if (be?.prix_unitaire) setPrixBurntEnds(String(be.prix_unitaire));
        }
      }).catch(() => {});
    }
  }, [batchId]);

  // Aperçu déduction inventaire — charger quand on arrive à l'étape finalisation
  useEffect(() => {
    if (etape === 6 && batchId) {
      api.get(`/brisket/batches/${batchId}/apercu-deduction`)
        .then(setApercuDeduction)
        .catch(() => {});
    }
  }, [etape, batchId]);

  // ─── CALCULS LIVE ─────────────────────────

  const poidsBrut = parseFloat(form1.poids_brut_initial_kg) || 0;

  // Calculs parage live
  const parures = parseFloat(formParage.poids_parures_kg) || 0;
  const equipe  = parseFloat(formParage.poids_equipe_kg)  || 0;
  const perte   = parseFloat(formParage.poids_perte_kg)   || 0;
  const poidsPareCalc = Math.max(0, poidsBrut - parures - equipe - perte);

  // Poids paré à utiliser (depuis API si disponible, sinon calcul live)
  const poidsPareRef = poidsPareAffiche != null
    ? poidsPareAffiche
    : ((batchData?.poids_pare_pre_cuisson_kg ?? batchData?.poids_pare) != null
        ? parseFloat(batchData.poids_pare_pre_cuisson_kg ?? batchData.poids_pare)
        : poidsPareCalc);

  const poidsCuit    = parseFloat(form2.poids_apres_cuisson_kg) || 0;
  const poidsCuitRef = batchData?.poids_apres_cuisson_kg != null
    ? (parseFloat(batchData.poids_apres_cuisson_kg) || 0)
    : poidsCuit;

  // Yield paré→cuit
  const yieldPareCuit = poidsPareRef > 0 && poidsCuitRef > 0
    ? Math.round(poidsCuitRef / poidsPareRef * 1000) / 10 : 0;
  // Yield brut→cuit
  const poidsBrutRef = batchData?.poids_brut_initial_kg != null
    ? (parseFloat(batchData.poids_brut_initial_kg) || 0)
    : poidsBrut;
  const yieldBrutCuit = poidsBrutRef > 0 && poidsCuitRef > 0
    ? Math.round(poidsCuitRef / poidsBrutRef * 1000) / 10 : 0;

  // Découpe live
  const totalTranchesG  = parseFloat(poidsTotalTranchesG) || 0;
  const nbTranches      = parseInt(nbPaquetsTranches) || 0;
  const moyenneTranche  = nbTranches > 0 ? Math.round(totalTranchesG / nbTranches) : 0;
  const nbC             = parseInt(nbChopped)   || 0;
  const nbB             = parseInt(nbBurntEnds) || 0;
  const totalChoppedG   = nbC * 350;
  const totalBurntEndsG = nbB * 200;
  const poidsVendableG  = totalTranchesG + totalChoppedG + totalBurntEndsG;
  const pertesG         = Math.max(0, poidsCuitRef * 1000 - poidsVendableG);
  const yieldGlobal     = poidsBrutRef > 0 ? Math.round(poidsVendableG / (poidsBrutRef * 1000) * 1000) / 10 : 0;

  // Barre de progression découpe — si pas de poids cuit saisi, on prend le total des paquets
  // comme dénominateur (le poids cuit est dérivé du poids total des paquets à la découpe).
  const totalCuitG       = poidsCuitRef > 0 ? poidsCuitRef * 1000 : poidsVendableG;
  const largeurTranches  = totalCuitG > 0 ? Math.min(100, totalTranchesG  / totalCuitG * 100) : 0;
  const largeurChopped   = totalCuitG > 0 ? Math.min(100 - largeurTranches, totalChoppedG / totalCuitG * 100) : 0;
  const largeurBurntEnds = totalCuitG > 0 ? Math.min(100 - largeurTranches - largeurChopped, totalBurntEndsG / totalCuitG * 100) : 0;

  // Donut chart data
  const donutData = [
    { name: 'Tranches',   value: Math.round(totalTranchesG),  couleur: COULEURS_REPARTITION.tranches },
    { name: 'Chopped',    value: Math.round(totalChoppedG),   couleur: COULEURS_REPARTITION.chopped },
    { name: 'Burnt Ends', value: Math.round(totalBurntEndsG), couleur: COULEURS_REPARTITION.burnt_ends },
    { name: 'Pertes',     value: Math.round(pertesG),          couleur: COULEURS_REPARTITION.pertes },
  ].filter(d => d.value > 0);

  // Reco de prix tranches (marge cible)
  // CORRECTIF 2026-06-01 : le coût total du batch est porté par la portion VENDABLE
  // (yield ~37%), pas par le poids brut. Le coût/g de produit vendable = coût_total / poids_vendable_g.
  // Un paquet de N grammes coûte donc (coût_total × N / poids_vendable_g), pas (prix_kg_brut × N).
  const prixKgBatch = batchData ? (parseFloat(batchData.prix_kg) || 0) : (parseFloat(form1.prix_kg) || 0);
  const coutEstime  = poidsBrutRef * prixKgBatch;
  const arrondiG    = parseFloat(poidsArrondiPaquetG) || moyenneTranche || 350;
  const coutParGTranche = (coutEstime > 0 && totalTranchesG > 0 && poidsVendableG > 0)
    ? coutEstime * arrondiG / poidsVendableG
    : null;
  const prixRecoTranche = (coutParGTranche != null && margeCible < 100)
    ? Math.ceil(coutParGTranche / (1 - margeCible / 100) * 100) / 100
    : null;
  // Reco prix chopped (350 g) + burnt ends (200 g) — même base : coût/g-vendable × poids paquet
  const coutGVendable = (coutEstime > 0 && poidsVendableG > 0) ? coutEstime / poidsVendableG : null;
  const coutParGChopped = coutGVendable != null ? coutGVendable * 350 : null;
  const coutParGBurnt   = coutGVendable != null ? coutGVendable * 200 : null;
  const prixRecoChopped = (coutParGChopped != null && margeCible < 100)
    ? Math.ceil(coutParGChopped / (1 - margeCible / 100) * 100) / 100
    : null;
  const prixRecoBurnt = (coutParGBurnt != null && margeCible < 100)
    ? Math.ceil(coutParGBurnt / (1 - margeCible / 100) * 100) / 100
    : null;

  // ─── ACTIONS ─────────────────────────────

  function showMsg(txt) { setMsg(txt); setTimeout(() => setMsg(''), 4000); }
  function showErr(txt) { setErreur(txt); setTimeout(() => setErreur(''), 6000); }

  async function soumettreEtape1(e) {
    e.preventDefault();
    if (!form1.date_prod)   { showErr('Date requise'); return; }
    if (!form1.operateurs)  { showErr('Veuillez sélectionner les opérateurs'); return; }
    if (!form1.nb_briskets) { showErr('Le nombre de briskets est obligatoire'); return; }
    if (submittingRef.current || loading) return;
    submittingRef.current = true;
    setLoading(true);
    try {
      const res = await api.post('/brisket/batches', {
        nom                  : form1.nom || undefined,
        date_prod            : form1.date_prod,
        operateurs           : form1.operateurs,
        fournisseur          : form1.fournisseur || null,
        prix_kg              : parseFloat(form1.prix_kg) || 0,
        nb_briskets          : parseInt(form1.nb_briskets),
        poids_brut_initial_kg: parseFloat(form1.poids_brut_initial_kg) || 0,
        notes                : form1.notes || null,
      });
      setBatchId(res.id);
      showMsg('Batch créé — passez au parage');
      chargerBatches();
      setEtape(2);
    } catch (err) {
      showErr(err.response?.data?.error || err.message || 'Erreur serveur');
    } finally {
      submittingRef.current = false;
      setLoading(false);
    }
  }

  async function soumettreParage(e) {
    e.preventDefault();
    setParageLoading(true); setParageErreur(''); setParageMsg('');
    try {
      const res = await api.post(`/brisket/batches/${batchId}/parage`, {
        poids_parures_kg: parseFloat(formParage.poids_parures_kg) || 0,
        poids_equipe_kg : parseFloat(formParage.poids_equipe_kg)  || 0,
        poids_perte_kg  : parseFloat(formParage.poids_perte_kg)   || 0,
      });
      const pare = (res.poids_pare_pre_cuisson_kg ?? res.poids_pare) != null
        ? (res.poids_pare_pre_cuisson_kg ?? res.poids_pare)
        : poidsPareCalc;
      setPoidsPareAffiche(pare);
      setParageMsg(`Parage enregistré — Poids paré : ${Number(pare).toFixed(2)} kg. Parures ajoutées à l'inventaire ✓`);
      // Refresh batchData
      api.get(`/brisket/batches/${batchId}`).then(setBatchData).catch(() => {});
    } catch (err) {
      setParageErreur(err.response?.data?.error || err.message || 'Erreur serveur');
    } finally {
      setParageLoading(false);
    }
  }

  async function soumettreEtape4(e) {
    e.preventDefault();
    setLoading(true);
    try {
      await api.put(`/brisket/batches/${batchId}/cuisson`, {
        poids_apres_cuisson_kg  : parseFloat(form2.poids_apres_cuisson_kg),
        heures_cuisson          : parseFloat(form2.heures_cuisson) || 0,
        temp_interne_f          : parseFloat(form2.temp_interne_f) || 0,
        poids_tallow_wrapping_ml: parseFloat(form2.poids_tallow_wrapping_ml) || 0,
        notes_qualite           : form2.notes_qualite || null,
      });
      const d = await api.get(`/brisket/batches/${batchId}`);
      setBatchData(d);
      showMsg('Données cuisson enregistrées');
      setEtape(5);
    } catch (err) {
      showErr(err.response?.data?.error || err.message || 'Erreur serveur');
    } finally { setLoading(false); }
  }

  async function finaliser() {
    if (!window.confirm('Confirmer la finalisation et le push vers WooCommerce ?')) return;
    if (submittingRef.current || loading) return;
    submittingRef.current = true;
    setLoading(true);
    try {
      const res = await api.post(`/brisket/batches/${batchId}/finaliser`, {
        ...finalForm,
        poids_total_tranches_g  : parseFloat(poidsTotalTranchesG)  || 0,
        nb_paquets_tranches     : parseInt(nbPaquetsTranches)       || 0,
        poids_arrondi_paquet_g  : parseFloat(poidsArrondiPaquetG)   || moyenneTranche || 0,
        prix_paquet             : parseFloat(prixPaquet)            || 0,
        nb_paquets_chopped      : parseInt(nbChopped)               || 0,
        nb_paquets_burnt        : parseInt(nbBurntEnds)             || 0,
        prix_chopped            : parseFloat(prixChopped)           || null,
        prix_burnt              : parseFloat(prixBurntEnds)         || null,
      });
      setDeductionsFinales(res.deductions || []);
      setAnalytiqueFinal(res.analytique || null);
      setRecosPrix(res.recos_prix || null);
      const nbT = parseInt(nbPaquetsTranches) || 0;
      const nbCh = parseInt(nbChopped) || 0;
      const nbBE = parseInt(nbBurntEnds) || 0;
      showMsg(`Batch finalisé — ${nbT} tranches, ${nbCh} BE350, ${nbBE} BEND250 pushés sur WC ✓`);
      chargerBatches();
      chargerStats();
      setEtape(7);
    } catch (err) {
      showErr(err.response?.data?.error || err.message || 'Erreur serveur');
    } finally {
      submittingRef.current = false;
      setLoading(false);
    }
  }

  async function supprimerBatch(id) {
    if (!window.confirm('Supprimer ce batch et toutes ses données ?')) return;
    try {
      await api.delete(`/brisket/batches/${id}`);
      chargerBatches();
      if (batchId === id) { setBatchId(null); setBatchData(null); setEtape(1); }
    } catch (err) { showErr(err.response?.data?.error || err.message); }
  }

  function nouveauBatch() {
    setBatchId(null); setBatchData(null);
    setNbChopped(''); setNbBurntEnds('');
    setPrixChopped(''); setPrixBurntEnds('');
    setPoidsTotalTranchesG(''); setNbPaquetsTranches('');
    setPoidsArrondiPaquetG(''); setPrixPaquet('');
    setFormParage({ poids_parures_kg: '', poids_equipe_kg: '', poids_perte_kg: '' });
    setPoidsPareAffiche(null);
    setParageMsg(''); setParageErreur('');
    setNomManuel(false);
    setForm1({ nom: '', date_prod: new Date().toISOString().slice(0, 10), operateurs: '', fournisseur: '', prix_kg: '', nb_briskets: '', poids_brut_initial_kg: '', notes: '' });
    setForm2({ poids_apres_cuisson_kg: '', heures_cuisson: '', temp_interne_f: '', poids_tallow_wrapping_ml: '', notes_qualite: '' });
    setApercuDeduction(null); setDeductionsFinales([]); setAnalytiqueFinal(null); setRecosPrix(null);
    setFinalForm(creerFormulaireTraiteFinal('brisket'));
    api.get('/batches/prochain-numero/brisket').then(r => {
      setNomAuto(r.nom);
      setForm1(f => ({ ...f, nom: r.nom }));
    }).catch(() => {});
    setEtape(1);
  }

  function demarrerDepuisPlan() {
    if (!planResult) return;
    setForm1(f => ({
      ...f,
      nb_briskets          : String(planResult.nb_briskets_estimes),
      poids_brut_initial_kg: String(planResult.kg_cru_necessaire),
    }));
    setOnglet('production');
    setEtape(1);
  }

  async function calculerPlan() {
    setPlanLoading(true);
    try {
      const res = await api.get(`/brisket/recommendation?kg_fini=${planKg}&format=${planFormat}`);
      setPlanResult(res);
    } catch (err) { showErr(err.response?.data?.error || err.message); }
    finally { setPlanLoading(false); }
  }

  // ─── ONGLET PRODUCTION ───────────────────

  function renderProduction() {
    // Étape 7 = succès post-finalisation (étape fantôme)
    if (etape === 7) {
      return (
        <div className="card finalise-card rise-in">
          <div className="finalise-icon">
            <Icon name="succes" size={32} weight="fill" />
          </div>
          <h3>Batch finalisé avec succès !</h3>
          {batchData && (
            <div className="yields-summary">
              <div className="yield-row">
                <span>Tranches (BRISTR)</span>
                <strong>{parseInt(nbPaquetsTranches) || batchData.nb_tranches || 0} sacs</strong>
              </div>
              <div className="yield-row">
                <span>Chopped BE350 (350g)</span>
                <strong>{parseInt(nbChopped) || batchData.nb_sacs_chopped || 0} sacs</strong>
              </div>
              <div className="yield-row">
                <span>Burnt Ends BEND250 (200g)</span>
                <strong>{parseInt(nbBurntEnds) || batchData.nb_sacs_burnt_ends || 0} sacs</strong>
              </div>
              <div className="yield-row">
                <span>Yield brut→cuit</span>
                <strong className="gold">{batchData.yield_brut_cuit_pct || yieldBrutCuit || '—'}%</strong>
              </div>
            </div>
          )}
          {deductionsFinales.length > 0 && (
            <div style={{ marginTop: 16, textAlign: 'left' }}>
              <h4 style={{ marginBottom: 10 }}>Inventaire déduit</h4>
              <table className="esc-table">
                <thead>
                  <tr><th>Ingrédient</th><th>Déduit</th><th>Stock restant</th></tr>
                </thead>
                <tbody>
                  {deductionsFinales.map(d => (
                    <tr key={d.matiere_id}>
                      <td>{d.nom}</td>
                      <td className="gold">−{d.quantite_deduite} {d.unite}</td>
                      <td className={d.statut === 'negatif' ? 'danger' : d.statut === 'bas' ? 'warning' : 'success'}>
                        {d.stock_apres} {d.unite}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {deductionsFinales.some(d => d.statut !== 'ok') && (
                <p className="warning" style={{ marginTop: 8, fontSize: 13 }}>
                  {deductionsFinales.filter(d => d.statut !== 'ok').length} ingrédient(s) à réapprovisionner
                </p>
              )}
            </div>
          )}
          <CoutRevient analytique={analytiqueFinal} />
          {batchId && <CourbeCuissonBatch batchId={batchId} produit="brisket" sessionActive={false} />}
          <button className="btn btn-primary" onClick={nouveauBatch} style={{ marginTop: 20 }}>Nouveau batch</button>
        </div>
      );
    }

    const isFinalise = batches.find(b => b.id === batchId)?.statut === 'finalise';
    const handleStepClick = (cible) => {
      if (!batchId && cible > 1) return;
      if (cible < etape) { setEtape(cible); return; }
      if (isFinalise) { setEtape(cible); return; }
      if (cible <= etape + 1) setEtape(cible);
    };

    return (
      <>
        {batchId && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
            <button className="btn btn-ghost btn--sm" onClick={nouveauBatch}>
              <Icon name="ajouter" size={14} />
              Nouveau batch
            </button>
          </div>
        )}
        <Stepper
          etape={etape}
          nbTracabiliteNonLies={nbTracabiliteNonLies}
          onStepClick={handleStepClick}
          hasBatch={!!batchId}
          isFinalise={isFinalise}
        />

        {/* ── ÉTAPE 1 — En-tête (création) ── */}
        {etape === 1 && !batchId && (
          <form className="card form-card rise-in" onSubmit={soumettreEtape1}>
            <div className="card-section-header">
              <Icon name="brisket" size={15} color="var(--muted)" />
              <span className="form-titre">Informations du batch</span>
            </div>
            <RecetteStockPanel produit="brisket" poidsCruKg={form1.poids_brut_initial_kg} />
            <div className="form-grid">
              <div className="field">
                <label>Numéro du batch</label>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input
                    value={form1.nom}
                    onChange={e => setForm1(f => ({ ...f, nom: e.target.value }))}
                    readOnly={!nomManuel}
                    style={{ flex: 1, opacity: nomManuel ? 1 : 0.75, background: nomManuel ? undefined : 'rgba(255,255,255,0.04)' }}
                    placeholder={nomAuto || 'Chargement…'}
                  />
                  <button
                    type="button"
                    className="btn btn-ghost btn--sm"
                    onClick={() => setNomManuel(m => !m)}
                    style={{ whiteSpace: 'nowrap' }}
                  >
                    {nomManuel
                      ? <><Icon name="retour" size={13} /> Auto</>
                      : <><Icon name="modifier" size={13} /> Modifier</>
                    }
                  </button>
                </div>
                <span className="muted" style={{ fontSize: 12 }}>Numéro auto-généré</span>
              </div>
              <div className="field">
                <label>Date production *</label>
                <DateInput value={form1.date_prod} onChange={v => setForm1(f => ({ ...f, date_prod: v }))} required />
              </div>
              <div className="field">
                <label>Opérateurs *</label>
                <select
                  value={form1.operateurs}
                  onChange={e => setForm1(f => ({ ...f, operateurs: e.target.value }))}
                  required
                >
                  <option value="">— Sélectionner —</option>
                  <option value="LCU">LCU</option>
                  <option value="ELA">ELA</option>
                  <option value="LCU+ELA">Les deux</option>
                </select>
              </div>
              <div className="field">
                <label>Fournisseur</label>
                <input value={form1.fournisseur} onChange={e => setForm1(f => ({ ...f, fournisseur: e.target.value }))} placeholder="Boucherie..." />
              </div>
              <div className="field">
                <label>Prix d'achat matière (cru) — $/kg</label>
                <input type="number" step="0.01" value={form1.prix_kg} onChange={e => setForm1(f => ({ ...f, prix_kg: e.target.value }))} placeholder="12.00" />
              </div>
              <div className="field">
                <label>Nb briskets achetés *</label>
                <input
                  type="number" step="1"
                  value={form1.nb_briskets}
                  onChange={e => setForm1(f => ({ ...f, nb_briskets: e.target.value }))}
                  placeholder="2"
                  required
                />
              </div>
              <div className="field">
                <label>Poids brut initial (kg, non paré) *</label>
                <input
                  type="number" step="0.01"
                  value={form1.poids_brut_initial_kg}
                  onChange={e => setForm1(f => ({ ...f, poids_brut_initial_kg: e.target.value }))}
                  placeholder="8.0"
                  required
                />
                <span className="muted" style={{ fontSize: 12 }}>Poids tel qu'acheté, avant parage</span>
              </div>
            </div>

            <div className="field full">
              <label>Notes</label>
              <textarea value={form1.notes} onChange={e => setForm1(f => ({ ...f, notes: e.target.value }))} rows={2} placeholder="Notes..." />
            </div>

            <div className="form-actions">
              <button type="submit" className="btn btn-primary" disabled={loading || !form1.operateurs || !form1.nb_briskets}>
                {loading ? 'Création...' : 'Créer le batch →'}
              </button>
            </div>
          </form>
        )}

        {/* ── ÉTAPE 1 BIS — Édition d'un batch existant ── */}
        {etape === 1 && batchId && (
          <EtapeBatchEditerBrisket
            batchId={batchId}
            batchData={batchData}
            isFinalise={isFinalise}
            onUpdated={(updated) => {
              setBatchData(updated);
              setForm1(f => ({
                ...f,
                nom                  : updated.nom || f.nom,
                date_prod            : updated.date_prod || f.date_prod,
                operateurs           : updated.operateurs || f.operateurs,
                fournisseur          : updated.fournisseur || f.fournisseur,
                prix_kg              : updated.prix_kg != null ? String(updated.prix_kg) : f.prix_kg,
                nb_briskets          : updated.nb_briskets != null ? String(updated.nb_briskets) : f.nb_briskets,
                poids_brut_initial_kg: updated.poids_brut_initial_kg != null ? String(updated.poids_brut_initial_kg) : f.poids_brut_initial_kg,
                notes                : updated.notes || f.notes,
              }));
              chargerBatches();
            }}
          />
        )}

        {/* ── ÉTAPE 2 — Parage ── */}
        {etape === 2 && batchId && (
          <form className="card form-card" onSubmit={soumettreParage}>
            <div className="card-section-header">
              <Icon name="parage" size={15} color="var(--muted)" />
              <span className="form-titre">Parage de la poitrine</span>
            </div>

            <div className="card-inner" style={{ marginBottom: 16 }}>
              <p style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 12, lineHeight: 1.6 }}>
                Saisissez les poids de chaque fraction retirée lors du parage.
                Le bouton <strong style={{ color: 'var(--text)' }}>Enregistrer le parage</strong> effectue immédiatement
                les mouvements d'inventaire : déduction de « Poitrine de bœuf » + ajout de « Parures de brisket ».
                Ce bouton est <strong style={{ color: 'var(--text)' }}>idempotent</strong> : vous pouvez le recliquer pour corriger.
              </p>
              <div className="calculs-live" style={{ marginTop: 0 }}>
                <div className="calcul-row">
                  <span>Poids brut initial (achat)</span>
                  <strong>{poidsBrutRef.toFixed(2)} kg</strong>
                </div>
              </div>
            </div>

            <div className="form-grid">
              <div className="field">
                <label>Poids parures (gras + gras dur) — kg</label>
                <input
                  type="number" step="0.01"
                  value={formParage.poids_parures_kg}
                  onChange={e => setFormParage(f => ({ ...f, poids_parures_kg: e.target.value }))}
                  placeholder="0.80"
                />
                <span className="muted" style={{ fontSize: 12 }}>→ Ajouté à « Parures de brisket » (pour Tallow + Saucisses plus tard)</span>
              </div>
              <div className="field">
                <label>Poids équipe / dégustation — kg</label>
                <input
                  type="number" step="0.01"
                  value={formParage.poids_equipe_kg}
                  onChange={e => setFormParage(f => ({ ...f, poids_equipe_kg: e.target.value }))}
                  placeholder="0.10"
                />
                <span className="muted" style={{ fontSize: 12 }}>Tracé pour yield seulement — aucun mouvement d'inventaire</span>
              </div>
              <div className="field">
                <label>Poids perte / déchet — kg</label>
                <input
                  type="number" step="0.01"
                  value={formParage.poids_perte_kg}
                  onChange={e => setFormParage(f => ({ ...f, poids_perte_kg: e.target.value }))}
                  placeholder="0.05"
                />
                <span className="muted" style={{ fontSize: 12 }}>Tracé pour yield seulement — aucun mouvement d'inventaire</span>
              </div>
            </div>

            {/* Calcul live poids paré */}
            {poidsBrutRef > 0 && (
              <div className="calculs-live">
                <div className="calcul-row">
                  <span>Poids brut initial</span>
                  <strong>{poidsBrutRef.toFixed(2)} kg</strong>
                </div>
                <div className="calcul-row">
                  <span>− Parures {poidsPareAffiche != null && <span className="success" style={{ fontSize: 11, display: 'inline-flex', alignItems: 'center', gap: 3 }}><Icon name="check" size={11} weight="bold" /> ajoutées à l'inventaire</span>}</span>
                  <strong className="muted">{parures.toFixed(2)} kg</strong>
                </div>
                <div className="calcul-row">
                  <span>− Équipe / perte <span className="muted" style={{ fontSize: 11 }}>(tracé, aucun mouvement)</span></span>
                  <strong className="muted">{(equipe + perte).toFixed(2)} kg</strong>
                </div>
                <div className="calcul-row" style={{ borderTop: '1px solid var(--border)', paddingTop: 6, marginTop: 4 }}>
                  <span>
                    <strong>Poids paré pré-cuisson</strong>
                    <span className="muted" style={{ display: 'block', fontSize: 11, fontWeight: 400 }}>
                      → part en cuisson · aucun ajout à l'inventaire
                    </span>
                  </span>
                  <strong className="gold" style={{ fontSize: 15 }}>
                    {(poidsPareAffiche != null ? Number(poidsPareAffiche) : poidsPareCalc).toFixed(2)} kg
                  </strong>
                </div>
                {poidsBrutRef > 0 && (
                  <div className="calcul-row">
                    <span>Yield parage</span>
                    <strong>
                      {Math.round(
                        (poidsPareAffiche != null ? poidsPareAffiche : poidsPareCalc)
                        / poidsBrutRef * 1000
                      ) / 10}%
                    </strong>
                  </div>
                )}
              </div>
            )}

            {parageMsg    && <p className="success" style={{ marginTop: 10, fontSize: 13 }}>{parageMsg}</p>}
            {parageErreur && <p className="danger"  style={{ marginTop: 10, fontSize: 13 }}>{parageErreur}</p>}

            <div className="form-actions">
              <button type="button" className="btn btn-ghost" onClick={() => setEtape(1)}>← Retour</button>
              <button type="submit" className="btn btn-ghost" disabled={parageLoading}>
                {parageLoading ? 'Enregistrement...' : 'Enregistrer le parage'}
              </button>
              <button type="button" className="btn btn-primary" onClick={() => setEtape(3)}>
                Étape suivante →
              </button>
            </div>
          </form>
        )}

        {/* ── ÉTAPE 3 — Traçabilité ── */}
        {etape === 3 && batchId && (
          <EtapeTracabilite
            batchId={batchId}
            batchNom={form1.nom || batchData?.nom}
            batchStatut={batches.find(b => b.id === batchId)?.statut}
            onNext={() => setEtape(4)}
            onBack={() => setEtape(2)}
            onNbNonLiesChange={setNbTracabiliteNonLies}
          />
        )}

        {/* ── ÉTAPE 4 — Cuisson ── */}
        {etape === 4 && (
          <>
          {batchId && (
            <PanneauTimerCuissonBrisket
              batchId={batchId}
              batchData={batchData}
              dateProd={form1.date_prod || batchData?.date_prod}
              onRefresh={() => batchId && api.get(`/brisket/batches/${batchId}`).then(setBatchData).catch(() => {})}
            />
          )}
          <form className="card form-card" onSubmit={soumettreEtape4}>
            <div className="card-section-header">
              <Icon name="fumage" size={15} color="var(--muted)" />
              <span className="form-titre">Cuisson (14–16 h)</span>
            </div>
            <div className="form-grid">
              <div className="field">
                <label>Poids après cuisson (kg)</label>
                <input type="number" step="0.01" value={form2.poids_apres_cuisson_kg}
                  onChange={e => setForm2(f => ({ ...f, poids_apres_cuisson_kg: e.target.value }))}
                  placeholder="optionnel" />
                <span className="muted" style={{ fontSize: 12 }}>Optionnel — le yield est calculé à l'étape Découpe via le poids total des paquets. À remplir seulement si tu pèses le brisket à la sortie du fumoir.</span>
              </div>
              <div className="field">
                <label>Heures de cuisson</label>
                <input type="number" step="0.5" value={form2.heures_cuisson}
                  onChange={e => setForm2(f => ({ ...f, heures_cuisson: e.target.value }))}
                  placeholder="14" />
              </div>
              <div className="field">
                <label>Temp. interne atteinte (°F)</label>
                <input type="number" step="1" value={form2.temp_interne_f}
                  onChange={e => setForm2(f => ({ ...f, temp_interne_f: e.target.value }))}
                  placeholder="203" />
              </div>
              <div className="field">
                <label>Tallow consommé au wrapping (ml)</label>
                <input type="number" step="1" value={form2.poids_tallow_wrapping_ml}
                  onChange={e => setForm2(f => ({ ...f, poids_tallow_wrapping_ml: e.target.value }))}
                  placeholder="150" />
                <span className="muted" style={{ fontSize: 12 }}>Beef Tallow utilisé pour le foil boat / wrapping</span>
              </div>
              <div className="field">
                <label>Notes qualité</label>
                <input value={form2.notes_qualite}
                  onChange={e => setForm2(f => ({ ...f, notes_qualite: e.target.value }))}
                  placeholder="Bark excellent..." />
              </div>
            </div>

            {poidsCuit > 0 && poidsPareRef > 0 && (
              <div className="calculs-live">
                <div className="calcul-row">
                  <span>Yield paré→cuit</span>
                  <strong className={yieldPareCuit >= 65 ? 'success' : yieldPareCuit >= 55 ? 'warning' : 'danger'}>
                    {yieldPareCuit}%
                  </strong>
                </div>
                <div className="calcul-row">
                  <span>Yield brut→cuit</span>
                  <strong className="muted">{yieldBrutCuit}%</strong>
                </div>
                <div className="calcul-row">
                  <span>Perte eau / cuisson</span>
                  <strong>{Math.max(0, poidsPareRef - poidsCuit).toFixed(2)} kg</strong>
                </div>
                {stats?.yield_cuisson_moyen && (
                  <div className="calcul-row">
                    <span>Yield cuisson moyen historique</span>
                    <strong className="muted">{stats.yield_cuisson_moyen}%</strong>
                  </div>
                )}
              </div>
            )}

            <div className="form-actions">
              <button type="button" className="btn btn-ghost" onClick={() => setEtape(3)}>← Retour</button>
              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? 'Sauvegarde...' : 'Étape suivante →'}
              </button>
            </div>

            {batchId && <CourbeCuissonBatch batchId={batchId} produit="brisket" sessionActive={true} />}
          </form>
          </>
        )}

        {/* ── ÉTAPE 5 — Découpe ── */}
        {etape === 5 && (
          <div className="card form-card">
            <div className="card-section-header">
              <Icon name="decoupe" size={15} color="var(--muted)" />
              <span className="form-titre">Découpe — répartition</span>
              {isFinalise && <span className="badge badge--warning" style={{ marginLeft: 12 }}><Icon name="verrou" size={12} /> Verrouillé</span>}
            </div>

            {/* Barre de progression */}
            <div className="barre-repartition-container card-inner">
              <div className="barre-stats">
                {poidsCuitRef > 0 ? (
                  <>
                    <span>Poids cuit disponible : <strong>{poidsCuitRef.toFixed(2)} kg ({(poidsCuitRef * 1000).toFixed(0)} g)</strong></span>
                    <span>Utilisé : <strong>{poidsVendableG.toFixed(0)} g</strong></span>
                    <span className={pertesG > 0 ? 'muted' : 'success'}>
                      Pertes estimées : <strong>{pertesG.toFixed(0)} g</strong>
                    </span>
                  </>
                ) : (
                  <span>Poids total des paquets : <strong>{(poidsVendableG / 1000).toFixed(2)} kg ({poidsVendableG.toFixed(0)} g)</strong> <span className="muted">— = poids cuit vendable</span></span>
                )}
              </div>
              <div className="barre-repartition">
                <div className="barre-segment" style={{ width: `${largeurTranches}%`, background: COULEURS_REPARTITION.tranches }} title={`Tranches : ${totalTranchesG.toFixed(0)}g`} />
                <div className="barre-segment" style={{ width: `${largeurChopped}%`, background: COULEURS_REPARTITION.chopped }} title={`Chopped : ${totalChoppedG}g`} />
                <div className="barre-segment" style={{ width: `${largeurBurntEnds}%`, background: COULEURS_REPARTITION.burnt_ends }} title={`Burnt Ends : ${totalBurntEndsG}g`} />
              </div>
              <div className="barre-legende">
                <span style={{ color: COULEURS_REPARTITION.tranches }}>Tranches</span>
                <span style={{ color: COULEURS_REPARTITION.chopped }}>Chopped</span>
                <span style={{ color: COULEURS_REPARTITION.burnt_ends }}>Burnt Ends</span>
                <span style={{ color: 'var(--muted)' }}>Pertes</span>
              </div>
            </div>

            {/* ── Tranches ── */}
            <div className="section-repartition card-inner">
              <h4>Tranches (BRISTR) — poids variable décidé à la découpe</h4>

              <div className="form-grid" style={{ marginBottom: 8 }}>
                <div className="field">
                  <label>Poids total pesé — toutes tranches (g)</label>
                  <input
                    type="number" step="1"
                    value={poidsTotalTranchesG}
                    onChange={e => setPoidsTotalTranchesG(e.target.value)}
                    placeholder="1800"
                    disabled={isFinalise}
                  />
                  <span className="muted" style={{ fontSize: 12 }}>Poids réel total — référence pour le yield</span>
                </div>
                <div className="field">
                  <label>Nombre de paquets</label>
                  <input
                    type="number" step="1"
                    value={nbPaquetsTranches}
                    onChange={e => setNbPaquetsTranches(e.target.value)}
                    placeholder="6"
                    disabled={isFinalise}
                  />
                </div>
              </div>

              {/* Calculateur poids moyen */}
              {moyenneTranche > 0 && (
                <div className="calculs-live" style={{ marginBottom: 12 }}>
                  <div className="calcul-row">
                    <span>Poids moyen / paquet (calculé)</span>
                    <strong className="gold">{moyenneTranche} g</strong>
                  </div>
                </div>
              )}

              {/* LCU fixe le poids arrondi et le prix */}
              <div className="form-grid" style={{ marginBottom: 8 }}>
                <div className="field">
                  <label>Poids arrondi affiché / paquet (g)</label>
                  <input
                    type="number" step="1"
                    value={poidsArrondiPaquetG}
                    onChange={e => setPoidsArrondiPaquetG(e.target.value)}
                    placeholder={moyenneTranche > 0 ? String(Math.round(moyenneTranche / 25) * 25) : '300'}
                    disabled={isFinalise}
                  />
                  <span className="muted" style={{ fontSize: 12 }}>Poids affiché sur WooCommerce (arrondi commercial)</span>
                </div>
                <div className="field">
                  <label>Prix par paquet ($)</label>
                  <div style={{ position: 'relative' }}>
                    <input
                      type="number" step="0.01"
                      value={prixPaquet}
                      onChange={e => setPrixPaquet(e.target.value)}
                      placeholder={prixRecoTranche != null ? `Reco : ${prixRecoTranche.toFixed(2)}` : '35.00'}
                      disabled={isFinalise}
                      style={{ width: '100%' }}
                    />
                  </div>
                  {/* Recommandation de prix */}
                  {prixRecoTranche != null && (
                    <div className="reco-prix-banner">
                      <span className="gold" style={{ fontWeight: 600 }}>Reco {margeCible}% marge : </span>
                      <span className="gold">{prixRecoTranche.toFixed(2)} $</span>
                      <span className="muted" style={{ marginLeft: 8 }}>
                        (coût estimé / paquet : {coutParGTranche != null ? coutParGTranche.toFixed(2) : '—'} $)
                      </span>
                      <button
                        type="button"
                        className="btn btn-ghost btn--sm"
                        style={{ marginLeft: 10 }}
                        onClick={() => setPrixPaquet(prixRecoTranche.toFixed(2))}
                        disabled={isFinalise}
                      >
                        Utiliser
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {parseInt(nbPaquetsTranches) > 0 && (
                <p className="calcul-inline">
                  {nbPaquetsTranches} sac(s) — {totalTranchesG.toFixed(0)} g total
                  {prixPaquet && ` — Revenu : ${(parseFloat(prixPaquet) * parseInt(nbPaquetsTranches)).toFixed(2)} $`}
                </p>
              )}
            </div>

            {/* ── Chopped ── */}
            <div className="section-repartition card-inner">
              <h4>Chopped Brisket (BE350 — <span className="gold">350 g</span>/sac)</h4>
              <p className="muted" style={{ fontSize: 13, marginBottom: 10 }}>Qualité moindre ou cuisson moins réussie. SKU BE350 — stock additif WooCommerce.</p>
              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                <div className="field" style={{ maxWidth: 200 }}>
                  <label>Nombre de sacs 350 g</label>
                  <input
                    type="number" step="1"
                    value={nbChopped}
                    onChange={e => setNbChopped(e.target.value)}
                    placeholder="0" min="0"
                    disabled={isFinalise}
                  />
                </div>
                <div className="field" style={{ maxWidth: 220 }}>
                  <label>Prix par paquet ($)</label>
                  <input
                    type="number" step="0.01"
                    value={prixChopped}
                    onChange={e => setPrixChopped(e.target.value)}
                    placeholder={prixRecoChopped != null ? `Reco : ${prixRecoChopped.toFixed(2)}` : '28.00'}
                    disabled={isFinalise}
                  />
                </div>
              </div>
              {nbC > 0 && (
                <p className="calcul-inline">Poids utilisé : {totalChoppedG} g</p>
              )}
              {prixRecoChopped != null && (
                <div className="reco-prix-banner" style={{ marginTop: 10 }}>
                  <span className="gold" style={{ fontWeight: 600 }}>Reco {margeCible}% marge : </span>
                  <span className="gold">{prixRecoChopped.toFixed(2)} $</span>
                  <span className="muted" style={{ marginLeft: 8, fontSize: 12 }}>
                    (coût estimé / paquet : {coutParGChopped != null ? coutParGChopped.toFixed(2) : '—'} $)
                  </span>
                  {!isFinalise && (
                    <button
                      type="button"
                      className="btn btn-ghost btn--sm"
                      style={{ marginLeft: 12 }}
                      onClick={() => setPrixChopped(prixRecoChopped.toFixed(2))}
                    >
                      Utiliser
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* ── Burnt Ends ── */}
            <div className="section-repartition card-inner">
              <h4>Burnt Ends (BEND250 — <span className="gold">200 g</span>/sac)</h4>
              <p className="muted" style={{ fontSize: 13, marginBottom: 10 }}>Pointe du brisket seulement — cubes caramélisés. SKU BEND250 — stock additif WooCommerce.</p>
              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                <div className="field" style={{ maxWidth: 200 }}>
                  <label>Nombre de sacs 200 g</label>
                  <input
                    type="number" step="1"
                    value={nbBurntEnds}
                    onChange={e => setNbBurntEnds(e.target.value)}
                    placeholder="0" min="0"
                    disabled={isFinalise}
                  />
                </div>
                <div className="field" style={{ maxWidth: 220 }}>
                  <label>Prix par paquet ($)</label>
                  <input
                    type="number" step="0.01"
                    value={prixBurntEnds}
                    onChange={e => setPrixBurntEnds(e.target.value)}
                    placeholder={prixRecoBurnt != null ? `Reco : ${prixRecoBurnt.toFixed(2)}` : '27.00'}
                    disabled={isFinalise}
                  />
                </div>
              </div>
              {nbB > 0 && (
                <p className="calcul-inline">Poids utilisé : {totalBurntEndsG} g</p>
              )}
              {prixRecoBurnt != null && (
                <div className="reco-prix-banner" style={{ marginTop: 10 }}>
                  <span className="gold" style={{ fontWeight: 600 }}>Reco {margeCible}% marge : </span>
                  <span className="gold">{prixRecoBurnt.toFixed(2)} $</span>
                  <span className="muted" style={{ marginLeft: 8, fontSize: 12 }}>
                    (coût estimé / paquet : {coutParGBurnt != null ? coutParGBurnt.toFixed(2) : '—'} $)
                  </span>
                  {!isFinalise && (
                    <button
                      type="button"
                      className="btn btn-ghost btn--sm"
                      style={{ marginLeft: 12 }}
                      onClick={() => setPrixBurntEnds(prixRecoBurnt.toFixed(2))}
                    >
                      Utiliser
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Donut chart */}
            {donutData.length > 0 && (
              <div className="donut-container card-inner">
                <h4>Répartition par format</h4>
                <ResponsiveContainer width="100%" height={260}>
                  <PieChart>
                    <Pie data={donutData} cx="50%" cy="50%" innerRadius={65} outerRadius={100}
                      dataKey="value" nameKey="name" paddingAngle={2}
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      labelLine={{ stroke: 'var(--muted)', strokeWidth: 1 }}>
                      {donutData.map((entry, i) => <Cell key={i} fill={entry.couleur} />)}
                    </Pie>
                    <Tooltip formatter={(v) => `${v}g`} contentStyle={{ background: 'var(--card)', border: '1px solid var(--border)', color: 'var(--text)', fontSize: 12 }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Yields 2 axes */}
            <div className="card-inner" style={{ marginBottom: 12 }}>
              <h4>Yields</h4>
              <div className="calculs-live" style={{ marginTop: 0 }}>
                <div className="calcul-row">
                  <span>Yield paré → cuit</span>
                  <strong className={yieldPareCuit >= 65 ? 'success' : yieldPareCuit >= 55 ? 'warning' : 'danger'}>
                    {yieldPareCuit > 0 ? `${yieldPareCuit}%` : '—'}
                  </strong>
                </div>
                <div className="calcul-row">
                  <span>Yield brut → cuit</span>
                  <strong className="muted">{yieldBrutCuit > 0 ? `${yieldBrutCuit}%` : '—'}</strong>
                </div>
                <div className="calcul-row">
                  <span>Yield global (vendable / brut acheté)</span>
                  <strong className={yieldGlobal >= 50 ? 'success' : yieldGlobal >= 35 ? 'warning' : 'danger'}>
                    {yieldGlobal > 0 ? `${yieldGlobal}%` : '—'}
                  </strong>
                </div>
              </div>
            </div>

            <div className="form-actions">
              <button type="button" className="btn btn-ghost" onClick={() => setEtape(4)}>← Retour</button>
              <button type="button" className="btn btn-primary" onClick={() => setEtape(6)}>
                Passer à la finalisation →
              </button>
            </div>
          </div>
        )}

        {/* ── ÉTAPE 6 — Finalisation ── */}
        {etape === 6 && !isFinalise && (
          <div className="card form-card">
            <div className="card-section-header">
              <Icon name="succes" size={15} color="var(--muted)" />
              <span className="form-titre">Finalisation et Push WooCommerce</span>
            </div>

            {/* Récapitulatif produits */}
            <div className="recapitulatif-final card-inner">
              <h4>Produits à créer / mettre à jour</h4>
              <div className="recap-ligne">
                <span className="badge badge--success">NOUVEAUX</span>
                <span>{parseInt(nbPaquetsTranches) || 0} sacs BRISTR (tranches) — produits uniques WC</span>
                {prixPaquet && (
                  <span className="gold" style={{ marginLeft: 'auto', fontWeight: 600 }}>{prixPaquet} $ / sac</span>
                )}
              </div>
              <div className="recap-ligne">
                <span className="badge badge--warning">STOCK +{parseInt(nbChopped) || 0}</span>
                <span>BE350 Chopped Brisket — 350 g / sac</span>
              </div>
              <div className="recap-ligne">
                <span className="badge badge--warning">STOCK +{parseInt(nbBurntEnds) || 0}</span>
                <span>BEND250 Burnt Ends — 200 g / sac</span>
              </div>
            </div>

            {/* Reco de prix — rappel pour chaque format */}
            {(prixRecoTranche != null || prixRecoChopped != null || prixRecoBurnt != null) && (
              <div className="rentabilite-banner">
                <h4 className="rentabilite-banner-titre">Recommandation de prix (marge {margeCible}%)</h4>
                <div className="calculs-live" style={{ marginTop: 0 }}>
                  {prixRecoTranche != null && (
                    <>
                      <div className="calcul-row">
                        <span>Tranches — prix recommandé</span>
                        <strong className="gold">{prixRecoTranche.toFixed(2)} $</strong>
                      </div>
                      <div className="calcul-row">
                        <span className="muted" style={{ paddingLeft: 12, fontSize: 12 }}>Prix saisi</span>
                        <strong>{prixPaquet ? `${parseFloat(prixPaquet).toFixed(2)} $` : <span className="muted">non saisi</span>}</strong>
                      </div>
                    </>
                  )}
                  {prixRecoChopped != null && nbC > 0 && (
                    <>
                      <div className="calcul-row">
                        <span>Chopped BE350 — prix recommandé</span>
                        <strong className="gold">{prixRecoChopped.toFixed(2)} $</strong>
                      </div>
                      <div className="calcul-row">
                        <span className="muted" style={{ paddingLeft: 12, fontSize: 12 }}>Prix saisi</span>
                        <strong>{prixChopped ? `${parseFloat(prixChopped).toFixed(2)} $` : <span className="muted">défaut 28,00 $</span>}</strong>
                      </div>
                    </>
                  )}
                  {prixRecoBurnt != null && nbB > 0 && (
                    <>
                      <div className="calcul-row">
                        <span>Burnt Ends BEND250 — prix recommandé</span>
                        <strong className="gold">{prixRecoBurnt.toFixed(2)} $</strong>
                      </div>
                      <div className="calcul-row">
                        <span className="muted" style={{ paddingLeft: 12, fontSize: 12 }}>Prix saisi</span>
                        <strong>{prixBurntEnds ? `${parseFloat(prixBurntEnds).toFixed(2)} $` : <span className="muted">défaut 27,00 $</span>}</strong>
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}

            <SectionProduitFini
              produit="brisket"
              form={finalForm}
              setForm={setFinalForm}
              batchId={batchId}
              batchStatut={batchData?.statut || batches.find(b => b.id === batchId)?.statut}
            />

            {/* Rentabilité — calcul live depuis aperçu (corrigé 2026-06-01) */}
            {apercuDeduction && (() => {
              const st = apercuDeduction.stats || {};
              // Coût total = coût recettes (bois, sel, poivre, étiquettes) + coût de la Poitrine
              // (déduite au parage, ajoutée séparément dans stats.cout_total_estime côté backend).
              const coutT  = st.cout_total_estime != null ? st.cout_total_estime : null;
              const nbU    = (parseInt(nbPaquetsTranches) || 0) + (parseInt(nbChopped) || 0) + (parseInt(nbBurntEnds) || 0);
              const coutPU = (coutT != null && nbU > 0) ? Math.round(coutT / nbU * 100) / 100 : null;
              const coutPK = st.cout_par_kg != null ? st.cout_par_kg : null;
              const yPct   = st.yield_brut_cuit != null ? st.yield_brut_cuit : (yieldGlobal > 0 ? yieldGlobal : null);
              // Yields paré→cuit et brut→cuit : utiliser les stats du backend (calculées avec poids
              // vendable) plutôt que la version locale qui dépend de poids_apres_cuisson_kg (optionnel).
              const yPC    = st.yield_pare_cuit != null ? st.yield_pare_cuit : (yieldPareCuit > 0 ? yieldPareCuit : null);
              const yBC    = st.yield_brut_cuit != null ? st.yield_brut_cuit : (yieldBrutCuit > 0 ? yieldBrutCuit : null);
              // Revenu : tranches (prix saisi) + chopped (prix saisi ou défaut 28) + burnt (prix saisi ou défaut 27)
              const pT = parseFloat(prixPaquet) || 0;
              const pC = parseFloat(prixChopped) || 28;
              const pB = parseFloat(prixBurntEnds) || 27;
              const revEst = pT * (parseInt(nbPaquetsTranches) || 0)
                           + pC * (parseInt(nbChopped) || 0)
                           + pB * (parseInt(nbBurntEnds) || 0);
              const mb     = (revEst > 0 && coutT != null) ? Math.round((revEst - coutT) / revEst * 1000) / 10 : null;
              return (
                <div className="rentabilite-grid-card">
                  <h4 className="rentabilite-titre">Rentabilité estimée</h4>
                  <div className="rentabilite-grid">
                    <div className="rentabilite-item">
                      <div className="rentabilite-label">Coût total batch</div>
                      <div className="rentabilite-valeur gold">{coutT != null ? `${coutT.toFixed(2)} $` : '—'}</div>
                    </div>
                    <div className="rentabilite-item">
                      <div className="rentabilite-label">Coût matières / sac</div>
                      <div className="rentabilite-valeur gold">{coutPU != null ? `${coutPU.toFixed(2)} $` : '—'}</div>
                    </div>
                    <div className="rentabilite-item">
                      <div className="rentabilite-label">Coût matières / kg brut</div>
                      <div className="rentabilite-valeur gold">{coutPK != null ? `${coutPK.toFixed(2)} $/kg` : '—'}</div>
                    </div>
                    <div className="rentabilite-item">
                      <div className="rentabilite-label">Yield global</div>
                      <div className={`rentabilite-valeur ${yPct == null ? 'muted' : yPct >= 50 ? 'success' : yPct >= 35 ? 'warning' : 'danger'}`}>
                        {yPct != null ? `${Number(yPct).toFixed(1)} %` : '—'}
                      </div>
                    </div>
                    <div className="rentabilite-item">
                      <div className="rentabilite-label">Yield paré→cuit</div>
                      <div className="rentabilite-valeur gold">{yPC != null ? `${Number(yPC).toFixed(1)} %` : '—'}</div>
                    </div>
                    <div className="rentabilite-item">
                      <div className="rentabilite-label">Yield brut→cuit</div>
                      <div className="rentabilite-valeur muted">{yBC != null ? `${Number(yBC).toFixed(1)} %` : '—'}</div>
                    </div>
                    <div className="rentabilite-item">
                      <div className="rentabilite-label">Revenu estimé</div>
                      <div className="rentabilite-valeur gold">{revEst > 0 ? `${revEst.toFixed(2)} $` : '—'}</div>
                    </div>
                    <div className="rentabilite-item">
                      <div className="rentabilite-label">Marge brute estimée</div>
                      <div className={`rentabilite-valeur ${mb == null ? 'muted' : mb >= margeCible ? 'success' : mb >= 40 ? 'warning' : 'danger'}`}>
                        {mb != null ? `${mb.toFixed(1)} %` : '—'}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Aperçu déduction inventaire */}
            {apercuDeduction?.deductions?.length > 0 && (
              <div className="card" style={{ marginTop: 16 }}>
                <h4 style={{ marginBottom: 10 }}>Aperçu — Déduction inventaire à la finalisation</h4>
                <p className="muted" style={{ fontSize: 13, marginBottom: 12 }}>
                  Ces quantités seront déduites de l'inventaire à la confirmation.
                </p>
                <table className="esc-table">
                  <thead>
                    <tr><th>Ingrédient</th><th>Déduit</th><th>Stock avant</th><th>Stock après</th></tr>
                  </thead>
                  <tbody>
                    {apercuDeduction.deductions.map(d => (
                      <tr key={d.matiere_id}>
                        <td>{d.nom}</td>
                        <td className="gold">−{d.quantite_deduite} {d.unite}</td>
                        <td className="muted">{d.stock_avant} {d.unite}</td>
                        <td className={d.statut === 'negatif' ? 'danger' : d.statut === 'bas' ? 'warning' : 'success'}>
                          {d.stock_apres} {d.unite}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {apercuDeduction.deductions.some(d => d.statut !== 'ok') && (
                  <p className="warning" style={{ marginTop: 8, fontSize: 13 }}>
                    {apercuDeduction.deductions.filter(d => d.statut !== 'ok').length} ingrédient(s) passeront sous le seuil après déduction.
                  </p>
                )}
              </div>
            )}

            <div className="form-actions">
              <button type="button" className="btn btn-ghost" onClick={() => setEtape(5)}>← Retour</button>
              <button type="button" className="btn btn-primary" disabled={loading} onClick={finaliser}>
                {loading ? 'Push en cours...' : 'Confirmer et pousser vers WooCommerce'}
              </button>
            </div>
          </div>
        )}

        {/* ── ÉTAPE 6 — Batch déjà finalisé ── */}
        {etape === 6 && isFinalise && (() => {
          const r = batchData?.repartition || {};
          const produits = batchData?.produits || [];
          const trProd = produits.filter(p => p.sku && p.sku.startsWith('BRISTR'));
          const chProd = produits.find(p => p.sku === 'BE350');
          const beProd = produits.find(p => p.sku === 'BEND250');
          const nbTr = r.nb_sacs_tranches || trProd.length || 0;
          const nbCh = r.nb_sacs_chopped || chProd?.quantite || 0;
          const nbBe = r.nb_sacs_burnt_ends || beProd?.quantite || 0;
          const prixTr = parseFloat(r.prix_tranches) || 0;
          const prixCh = parseFloat(chProd?.prix_unitaire) || 0;
          const prixBe = parseFloat(beProd?.prix_unitaire) || 0;
          const revTotal = parseFloat(batchData?.revenu_total_reel) || (prixTr * nbTr + prixCh * nbCh + prixBe * nbBe);
          const coutTotal = parseFloat(batchData?.cout_total) || parseFloat(batchData?.cout_matieres) || 0;
          const margeBrutePct = batchData?.marge_brute_pct != null ? Number(batchData.marge_brute_pct) : null;
          const yldGlobal  = batchData?.yield_global_pct != null ? Number(batchData.yield_global_pct) : null;
          const yldPareCuit = batchData?.yield_pare_cuit != null ? Number(batchData.yield_pare_cuit) : (batchData?.yield_pare_cuit_pct != null ? Number(batchData.yield_pare_cuit_pct) : null);
          const yldBrutCuit = batchData?.yield_brut_cuit != null ? Number(batchData.yield_brut_cuit) : (batchData?.yield_brut_cuit_pct != null ? Number(batchData.yield_brut_cuit_pct) : yldGlobal);
          const yldCuisson  = batchData?.yield_cuisson_pct != null ? Number(batchData.yield_cuisson_pct) : null;
          const coutParUnite = parseFloat(batchData?.cout_par_unite) || null;
          const coutParKg    = parseFloat(batchData?.cout_par_kg)    || null;
          const nbSacsTotal  = nbTr + nbCh + nbBe;

          return (
            <>
              <div className="card form-card">
                <div className="card-section-header">
                  <Icon name="verrou" size={15} color="var(--muted)" />
                  <span className="form-titre">Batch finalisé</span>
                </div>
                <p className="muted" style={{ marginBottom: 16 }}>Ce batch a été finalisé et pushé vers WooCommerce. Toutes les données sont verrouillées.</p>

                {/* Produits créés */}
                <div className="rentabilite-grid-card" style={{ marginBottom: 16 }}>
                  <h4 className="rentabilite-titre">Produits créés</h4>
                  <div className="calculs-live" style={{ marginTop: 0 }}>
                    {nbTr > 0 && (
                      <div className="calcul-row">
                        <span>{nbTr} sacs BRISTR (tranches)</span>
                        <strong>{prixTr ? `${prixTr.toFixed(2)} $ / sac` : '—'}</strong>
                      </div>
                    )}
                    {nbCh > 0 && (
                      <div className="calcul-row">
                        <span>{nbCh} sacs BE350 — Chopped 350 g</span>
                        <strong>{prixCh ? `${prixCh.toFixed(2)} $ / sac` : '—'}</strong>
                      </div>
                    )}
                    {nbBe > 0 && (
                      <div className="calcul-row">
                        <span>{nbBe} sacs BEND250 — Burnt Ends 200 g</span>
                        <strong>{prixBe ? `${prixBe.toFixed(2)} $ / sac` : '—'}</strong>
                      </div>
                    )}
                    <div className="calcul-row" style={{ borderTop: '1px solid var(--border)', paddingTop: 6, marginTop: 4 }}>
                      <strong>Total paquets</strong>
                      <strong className="gold">{nbSacsTotal}</strong>
                    </div>
                  </div>
                </div>

                {/* Rentabilité finale (valeurs persistées dans la DB) */}
                <div className="rentabilite-grid-card">
                  <h4 className="rentabilite-titre">Rentabilité finale</h4>
                  <div className="rentabilite-grid">
                    <div className="rentabilite-item">
                      <div className="rentabilite-label">Coût total batch</div>
                      <div className="rentabilite-valeur gold">{coutTotal > 0 ? `${coutTotal.toFixed(2)} $` : '—'}</div>
                    </div>
                    <div className="rentabilite-item">
                      <div className="rentabilite-label">Coût matières / sac</div>
                      <div className="rentabilite-valeur gold">{coutParUnite != null ? `${coutParUnite.toFixed(2)} $` : '—'}</div>
                    </div>
                    <div className="rentabilite-item">
                      <div className="rentabilite-label">Coût matières / kg brut</div>
                      <div className="rentabilite-valeur gold">{coutParKg != null ? `${coutParKg.toFixed(2)} $/kg` : '—'}</div>
                    </div>
                    <div className="rentabilite-item">
                      <div className="rentabilite-label">Yield global</div>
                      <div className={`rentabilite-valeur ${yldGlobal == null ? 'muted' : yldGlobal >= 50 ? 'success' : yldGlobal >= 35 ? 'warning' : 'danger'}`}>
                        {yldGlobal != null ? `${yldGlobal.toFixed(1)} %` : '—'}
                      </div>
                    </div>
                    <div className="rentabilite-item">
                      <div className="rentabilite-label">Yield paré→cuit</div>
                      <div className="rentabilite-valeur gold">{yldPareCuit != null ? `${yldPareCuit.toFixed(1)} %` : (yldCuisson != null ? `${yldCuisson.toFixed(1)} %` : '—')}</div>
                    </div>
                    <div className="rentabilite-item">
                      <div className="rentabilite-label">Yield brut→cuit</div>
                      <div className="rentabilite-valeur muted">{yldBrutCuit != null ? `${yldBrutCuit.toFixed(1)} %` : '—'}</div>
                    </div>
                    <div className="rentabilite-item">
                      <div className="rentabilite-label">Revenu total</div>
                      <div className="rentabilite-valeur gold">{revTotal > 0 ? `${revTotal.toFixed(2)} $` : '—'}</div>
                    </div>
                    <div className="rentabilite-item">
                      <div className="rentabilite-label">Marge brute</div>
                      <div className={`rentabilite-valeur ${margeBrutePct == null ? 'muted' : margeBrutePct >= margeCible ? 'success' : margeBrutePct >= 40 ? 'warning' : 'danger'}`}>
                        {margeBrutePct != null ? `${margeBrutePct.toFixed(1)} %` : '—'}
                      </div>
                    </div>
                  </div>
                </div>

                <CoutRevient analytique={analytiqueFinal} />
                {batchId && <CourbeCuissonBatch batchId={batchId} produit="brisket" sessionActive={false} />}

                <div className="form-actions">
                  <button type="button" className="btn btn-ghost" onClick={() => setEtape(5)}>← Retour Découpe</button>
                  <button type="button" className="btn btn-primary" onClick={nouveauBatch}>Nouveau batch</button>
                </div>
              </div>
            </>
          );
        })()}
      </>
    );
  }

  // ─── ONGLET PLANIFICATEUR ────────────────

  function renderPlanificateur() {
    return (
      <div className="card form-card">
        <div className="card-section-header">
          <Icon name="analytics" size={15} color="var(--muted)" />
          <span className="form-titre">Planificateur — Recommandation d'achat</span>
        </div>

        <div className="form-grid" style={{ alignItems: 'flex-end' }}>
          <div className="field">
            <label>Quantité désirée (kg fini)</label>
            <input type="number" step="0.5" value={planKg} onChange={e => setPlanKg(e.target.value)} placeholder="5" min="0.5" />
          </div>
          <div className="field">
            <label>Format cible</label>
            <select value={planFormat} onChange={e => setPlanFormat(e.target.value)}>
              <option value="chopped">Chopped (BE350)</option>
              <option value="burnt_ends">Burnt Ends (BEND250)</option>
              <option value="tranches">Tranches (BRISTR)</option>
            </select>
          </div>
          <div className="field">
            <button type="button" className="btn btn-primary" onClick={calculerPlan} disabled={planLoading}>
              {planLoading ? 'Calcul...' : 'Calculer'}
            </button>
          </div>
        </div>

        {planResult && (
          <div className="plan-resultat card-inner">
            <div className="plan-titre">
              <h4>Recommandation <BadgeConfiance confiance={planResult.confiance} /></h4>
              <span className="muted" style={{ fontSize: 12 }}>Basé sur {planResult.nb_batches_historique} batch(es) — yield moyen {planResult.yield_utilise}%</span>
            </div>

            <div className="plan-grid">
              <div className="plan-item">
                <span>Briskets à acheter</span>
                <strong className="gold">{planResult.nb_briskets_estimes} (~4 kg chacun)</strong>
              </div>
              <div className="plan-item">
                <span>Poids cru total</span>
                <strong>{planResult.kg_cru_necessaire} kg</strong>
              </div>
              <div className="plan-item">
                <span>Poids vendable estimé</span>
                <strong>{planResult.kg_fini_desire} kg</strong>
              </div>
              <div className="plan-item">
                <span>Coût estimé</span>
                <strong>{planResult.cout_estime}$</strong>
              </div>
              <div className="plan-item">
                <span>Revenus potentiels</span>
                <strong className="gold">{planResult.revenus_potentiels}$</strong>
              </div>
              <div className="plan-item">
                <span>Marge estimée</span>
                <strong className={planResult.marge_estimee_pct >= margeCible ? 'success' : 'warning'}>{planResult.marge_estimee_pct}%</strong>
              </div>
            </div>

            {planResult.confiance === 'faible' && (
              <p className="muted" style={{ fontSize: 12, marginTop: 8 }}>
                Confiance faible — moins de 3 batches historiques. Les estimations sont basées sur des valeurs par défaut (yield 60%).
              </p>
            )}

            <div className="form-actions" style={{ marginTop: 16 }}>
              <button type="button" className="btn btn-primary" onClick={demarrerDepuisPlan}>
                Démarrer ce batch →
              </button>
            </div>
          </div>
        )}

        {stats && stats.nb_batches > 0 && (
          <div className="stats-historiques card-inner" style={{ marginTop: 24 }}>
            <h4>Historique yields ({stats.nb_batches} batch{stats.nb_batches > 1 ? 'es' : ''})</h4>
            <div className="plan-grid">
              <div className="plan-item"><span>Yield cuisson moyen (paré→cuit)</span><strong>{stats.yield_cuisson_moyen}%</strong></div>
              <div className="plan-item"><span>Yield global moyen</span><strong className="gold">{stats.yield_global_moyen}%</strong></div>
            </div>
            {stats.meilleur_batch && (
              <div className="calcul-row">
                <span>Meilleur batch</span>
                <strong className="success">{stats.meilleur_batch.nom} ({stats.meilleur_batch.yield_global}%)</strong>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  // ─── LISTE DES BATCHES ────────────────────

  function renderListeBatches() {
    if (batches.length === 0) return null;
    return (
      <div className="card" style={{ marginTop: 24 }}>
        <h3 className="card-title">Batches brisket</h3>
        <table className="esc-table">
          <thead>
            <tr>
              <th>Batch</th><th>Date</th><th>Poids brut</th><th>Fournisseur</th><th>Tranches</th><th>BE350</th><th>BEND250</th><th>Yield brut→cuit</th><th>Statut</th><th></th>
            </tr>
          </thead>
          <tbody>
            {batches.map(b => (
              <tr key={b.id}>
                <td style={{ fontWeight: 600 }}>{b.nom}</td>
                <td>{b.date_prod}</td>
                <td>{b.poids_brut_initial_kg || b.poids_cru_total_kg ? `${b.poids_brut_initial_kg || b.poids_cru_total_kg} kg` : '—'}</td>
                <td className="muted">{b.fournisseur || '—'}</td>
                <td>{b.nb_tranches ?? '—'}</td>
                <td>{b.nb_sacs_chopped ?? '—'}</td>
                <td>{b.nb_sacs_burnt_ends ?? '—'}</td>
                <td className="gold">{b.yield_brut_cuit_pct || b.yield_global_pct ? `${b.yield_brut_cuit_pct || b.yield_global_pct}%` : '—'}</td>
                <td>
                  <span className={`badge ${b.statut === 'finalise' ? 'badge--success' : 'badge--warning'}`}>
                    {b.statut === 'finalise' ? 'Finalisé' : 'En cours'}
                  </span>
                  {b.nb_tracabilite_manquante > 0 && (
                    <span className="badge-tracabilite-warning" style={{ marginLeft: 6 }}>
                      {b.nb_tracabilite_manquante} sans facture
                    </span>
                  )}
                </td>
                <td>
                  {b.statut === 'finalise' && (
                    <button className="btn btn-ghost btn--sm" onClick={() => setBatchAnalytique(b)} title="Analytique">
                      <Icon name="analytics" size={14} />
                    </button>
                  )}
                  <button
                    className="btn btn-ghost btn--sm"
                    title="Reprendre ce batch"
                    onClick={() => {
                      setBatchId(b.id);
                      setEtape(b.statut === 'finalise' ? 6 : 2);
                    }}
                    style={{ marginLeft: 4 }}
                  >
                    <Icon name="retour" size={14} />
                  </button>
                  <button className="btn-icon-delete" title="Supprimer" onClick={() => supprimerBatch(b.id)} style={{ marginLeft: 4 }}>
                    <Icon name="fermer" size={13} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  // ─── RENDU ───────────────────────────────

  return (
    <div className="production-brisket">
      {batchAnalytique && (
        <div className="modal-overlay" onClick={() => setBatchAnalytique(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 860, maxHeight: '90vh', overflowY: 'auto' }}>
            <BatchAnalytiquePanel batch={batchAnalytique} onFermer={() => setBatchAnalytique(null)} />
          </div>
        </div>
      )}
      <div className="page-header">
        <div>
          <h1 className="page-title">Production — Brisket</h1>
          <p className="page-subtitle">Trim · Parage · Cuisson · Découpe · WooCommerce</p>
        </div>
      </div>

      {msg    && (
        <div className="alert alert-success">
          <Icon name="succes" size={15} />{msg}
        </div>
      )}
      {erreur && (
        <div className="alert alert-danger">
          <Icon name="erreur" size={15} />{erreur}
        </div>
      )}

      <div className="onglets">
        <button
          className={`onglet ${onglet === 'production' ? 'active' : ''}`}
          onClick={() => setOnglet('production')}
        >Production</button>
        <button
          className={`onglet ${onglet === 'planificateur' ? 'active' : ''}`}
          onClick={() => setOnglet('planificateur')}
        >Planificateur</button>
      </div>

      {onglet === 'production'    && renderProduction()}
      {onglet === 'planificateur' && renderPlanificateur()}

      {renderListeBatches()}
    </div>
  );
}
